import { useCallback, useEffect, useState } from 'react';

import { TextField } from '@mui/material';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { selectCurrentEntityType, selectIsDialogOpen,setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserProfileDeleteMembership } from 'common/services/api/UserProfileApps/UserProfileApps.thunk';
import { UserDetails } from 'common/types/Entities';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { clearUserProfileAcls } from 'features/userProfileAcls/userProfileAclsSlice';
import { clearUserProfileClaims } from 'features/userProfileClaims/userProfileClaimsSlice';
import { clearUserProfileGlobalRoles } from 'features/userProfileGlobalRoles/userProfileGlobalRolesSlice';
import { clearUserProfileRoles } from 'features/userProfileRoles/userProfileRolesSlice';
import { clearUserProfileUserGroups } from 'features/userProfileUserGroups/userProfileUserGroupsSlice';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { DeleteDialogContentProps, DrawerEntitiesNames } from '../../common/types/TypeDefs';

import { updateUserProfileTenant } from './helpers';
import { IUserProfileTenant } from './UserProfileApps';

const customWarningMessage = 'This action will remove all permissions for this user under this tenant and cannot be undone.';
const succesDeleteMessage = 'This user has been removed from this tenant.';

export function UserProfileAppsTenantsDeleteDialog({
    editingRow,
    updatePageCallback,
}: Readonly<DeleteDialogContentProps<{ appSuiteName: string; tenant: IUserProfileTenant; } | null>>) {
    const currentDialogEntity = useAppSelector(selectCurrentEntityType);
    const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(true);
    const { appSuiteName, tenant } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const isDeleteDialogOpened = useAppSelector(selectIsDialogOpen);

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        dispatch(clearUserProfileGlobalRoles());
        dispatch(clearUserProfileUserGroups());
        dispatch(clearUserProfileRoles());
        dispatch(clearUserProfileClaims());
        dispatch(clearUserProfileAcls());
        enqueueSnackbar(succesDeleteMessage, { variant: 'success' });
        setProcessing(false);
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('tenant membership from this user', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const userDetails = useAppSelector(selectUserDetails);
    const getUserFullName = (userDetailsArg: UserDetails | null): string => {
        const UNKNOWN_USER_DEFAULT_FULL_NAME = 'Unknown User';
        const { firstName='', lastName='' } = userDetailsArg ?? {};
        const existingUserFullName = `${firstName} ${lastName}`;
        return firstName && lastName ? existingUserFullName : UNKNOWN_USER_DEFAULT_FULL_NAME;
    };

    useEffect(() => {
        if(!isDeleteDialogOpened) {
            setDeleteButtonDisabled(true);
        }
    }, [isDeleteDialogOpened]);

    const userProfileTenantMembershipDeleteCallback = useCallback(() => {
        if (userDetails?.userId && tenant?.id) {
            const requestPayloadOptions = {
                tenantId: tenant?.id || '',
                userId: userDetails?.userId || ''
            };
            setProcessing(true);
            dispatch(deleteUserProfileDeleteMembership(requestPayloadOptions))
                .unwrap()
                .then(() => {
                    updatePageCallback?.();
                    updateUserProfileTenant(requestPayloadOptions);
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                })
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [userDetails?.userId, tenant?.id]);

    if (currentDialogEntity !== DrawerEntitiesNames.UserTenant) {
        return null;
    }

    return  (
        <DeleteDialog
            deleteCallback={userProfileTenantMembershipDeleteCallback}
            processing={processing ?? false}
            title={'user from this tenant'}
            customWarningMessage={customWarningMessage}
            deleteButtonDisabled={deleteButtonDisabled}
        >
            <DeleteDialogCustomField
                fieldName='Name'
                fieldValue={getUserFullName(userDetails)}
            />
            <DeleteDialogCustomField
                fieldName='Tenant Name'
                fieldValue={tenant?.name ?? ''}
            />
            <DeleteDialogCustomField
                fieldName='Application Suite Name'
                fieldValue={appSuiteName ?? ''}
            />
            <TextField
                inputProps={{ 'data-testid': 'text-field-test-id' }}
                onChange={(e)=> {
                    if (e.target.value === 'REMOVE') {
                        setDeleteButtonDisabled(false);
                    } else {
                        setDeleteButtonDisabled(true);
                    }
                }}
                sx={{ marginTop: '1rem', display: 'flex', '& .MuiOutlinedInput-root': { height: '35px' } }}
                id="outlined-helperText"
                helperText="Type  'REMOVE'  to confirm"
            />
        </DeleteDialog>
    );
}
